<template>
  <BaseModal
    :close-button="false"
    class="print-template-library-modal"
    description="Wybierz styl, który najlepiej pasuje do Twojego pacjenta."
    :has-divider="false"
    :hide-footer="true"
    :open="true"
    title="Wybierz szablon PDF"
    @close="emit('close')"
  >
    <template #icon>
      <div class="print-template-library__toolbar">
        <Button color="gray" variant="text" @click="emit('close')">
          <template #leftIcon><Icon name="ArrowLeft" /></template>
          Powrót do ustawień
        </Button>
      </div>
    </template>

    <main class="print-template-library">
      <div v-if="isPending" class="print-template-library__state">
        <BaseLoader class="base-loader--size-24" />
        Ładowanie biblioteki szablonów...
      </div>
      <div v-else-if="isError" class="print-template-library__state">
        <p>Nie udało się pobrać biblioteki szablonów PDF.</p>
        <Button color="gray" size="sm" variant="secondary" @click="refetch">
          Spróbuj ponownie
        </Button>
      </div>
      <div v-else-if="themes.length === 0" class="print-template-library__state">
        Brak dostępnych szablonów PDF.
      </div>
      <div v-else class="print-template-library__grid">
        <div
          v-for="theme in themes"
          :key="theme.value"
          :aria-label="`Wybierz szablon ${theme.label}`"
          class="print-template-library__card"
          role="button"
          tabindex="0"
          @click="selectTheme(theme.value)"
          @keydown.enter.self.prevent="selectTheme(theme.value)"
          @keydown.space.self.prevent="selectTheme(theme.value)"
        >
          <div
            class="print-template-library__preview"
            :class="{ 'print-template-library__preview--selected': value === theme.value }"
          >
            <Badge
              v-if="value === theme.value"
              class="print-template-library__badge"
              color="brand"
              icon="CheckCircle"
              title="Aktualnie wybrany"
            />
            <img :alt="theme.label" :src="theme.preview_png">
            <div class="print-template-library__actions">
              <Button
                color="gray"
                size="sm"
                variant="secondary"
                @click.stop="previewTheme(theme.preview_pdf)"
              >
                <template #leftIcon><Icon name="Eye" /></template>
                Podgląd
              </Button>
              <Button size="sm" @click.stop="selectTheme(theme.value)">
                <template #leftIcon><Icon name="Check" /></template>
                Zastosuj
              </Button>
            </div>
          </div>
          <p :class="{ 'print-template-library__label--selected': value === theme.value }">
            {{ theme.label }}
          </p>
        </div>
      </div>
    </main>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { useQuery } from '@tanstack/vue-query'
  import { computed } from 'vue'

  import BaseLoader from '@/components/BaseLoader.vue'
  import { Badge, BaseModal, Button, Icon } from '@/components/ui'
  import { api } from '@/services/apiService'

  type ThemeOption = {
    label: string
    preview_pdf: string
    preview_png: string
    value: string
  }

  type PatientSettingsOptions = {
    choices: {
      theme: ThemeOption[]
    }
  }

  const props = defineProps<{ patientId?: number; value: string }>()
  const emit = defineEmits<{ close: []; select: [value: string] }>()

  const { data, isError, isPending, refetch } = useQuery({
    queryFn: () => api.options<PatientSettingsOptions>(
      props.patientId ? `/dietitian/patient/${props.patientId}/` : '/dietitian/patient/',
    ),
    queryKey: ['print-template-library', props.patientId],
    select: response => response.data,
  })
  const themes = computed(() => data.value?.choices.theme ?? [])

  const previewTheme = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const selectTheme = (value: string) => {
    emit('select', value)
    emit('close')
  }
</script>

<style scoped lang="scss">
  @use '@repo/ui/typography.scss' as typography;

  :global(.print-template-library-modal.ui-dialog__panel) {
    inset: 0;
    width: 100%;
    height: 100dvh;
    max-width: none;
    max-height: none;
    margin: 0;
    overflow-y: auto;
    overscroll-behavior: none;
    border-radius: 0;
    box-shadow: none;
  }

  :global(.print-template-library-modal .ui-modal-header) {
    display: contents;
  }

  .print-template-library__toolbar,
  :global(.print-template-library-modal .ui-modal-header > div:last-child),
  :global(.print-template-library-modal .ui-modal-content) {
    width: 100%;
    max-width: 1216px;
    margin-inline: auto;
    box-sizing: border-box;
  }

  .print-template-library__toolbar {
    display: block;
    padding: 72px 32px 0;
  }

  :global(.print-template-library-modal .ui-modal-header > div:last-child) {
    padding-inline: 32px;
    margin-top: 10px;
  }

  :global(.print-template-library-modal .ui-modal-content) {
    padding: 20px 32px 96px;
    overflow: visible;
  }

  .print-template-library {
    padding-top: 32px;
    border-top: 1px solid var(--color-border-border-secondary);
  }

  .print-template-library__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 30px 20px;
    padding: 0 16px;
  }

  .print-template-library__card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    min-width: 0;
    cursor: pointer;
    outline: none;

    > p {
      @include typography.font-style('text-md', 'semibold');

      margin: 0;
      color: var(--color-text-text-secondary-700);
      text-align: center;
      transition: color 0.2s ease;
    }

    &:hover > p,
    &:focus-visible > p,
    > .print-template-library__label--selected {
      color: var(--color-text-text-brand-primary-900);
    }
  }

  .print-template-library__preview {
    position: relative;
    width: 100%;
    aspect-ratio: 210 / 297;
    overflow: hidden;
    background: var(--color-background-bg-secondary);
    border: 1px solid var(--color-border-border-primary);
    border-radius: var(--radius-lg);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .print-template-library__preview--selected {
    border-color: var(--color-border-border-brand);
    box-shadow: 0 0 0 3px var(--color-brand-500);
  }

  .print-template-library__badge {
    position: absolute;
    top: 20px;
    right: 16px;
    z-index: 1;
  }

  .print-template-library__actions {
    position: absolute;
    inset: 0;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    visibility: hidden;
    background: rgb(52 64 84 / 60%);
    opacity: 0;
    transition: opacity 0.2s ease, visibility 0.2s ease;
  }

  .print-template-library__card:hover .print-template-library__actions,
  .print-template-library__card:focus-within .print-template-library__actions {
    pointer-events: auto;
    visibility: visible;
    opacity: 1;
  }

  .print-template-library__state {
    @include typography.font-style('text-sm', 'regular');

    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    color: var(--color-text-text-secondary-700);
    text-align: center;
  }

  @media (width <= 900px) {
    .print-template-library__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (width <= 540px) {
    .print-template-library__toolbar {
      padding: 32px 16px 0;
    }

    :global(.print-template-library-modal .ui-modal-header > div:last-child) {
      padding-inline: 16px;
    }

    :global(.print-template-library-modal .ui-modal-content) {
      padding: 20px 16px 64px;
    }

    .print-template-library__grid {
      grid-template-columns: 1fr;
      padding-inline: 0;
    }
  }
</style>
